import React, { useMemo } from 'react'
import moment from 'moment'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import config from 'constants/config'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import Caption from 'components/Luxkit/Typography/Caption'
import { connect } from 'react-redux'
import { getCustomerType } from 'lib/customer/peopleLikeMeUtils'
import useHotelOfferSales from 'hooks/useHotelOfferSales'
import { LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import PeopleLikeMeSalesBanner from '../../../SearchV2/Components/PeopleLikeMe/PeopleLikeMeSalesBanner'
import ViewBundleOffer from './ViewBundleOffer'
import SearchTilePropertyDetails from './TileSections/SearchTilePropertyDetails'
import SearchTilePriceDetails from './TileSections/SearchTilePriceDetails'
import cn from 'clsx'
import { Main, MainImageSegment, OfferOverlayContainer, StyledOfferBadge, DetailSegment, StyledCSSBreakpoint, ViewBundleOfferWrapper } from './SearchTileStyles'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import { ImageParams } from 'components/Common/ResponsiveImage'
import { Location } from 'history'

const imageParams: ImageParams = {
  tabletAspectRatio: '16:9',
  tabletWidth: '100vw',
  desktopAspectRatio: '16:9',
  desktopWidth: '67vw',
  largeDesktopAspectRatio: '16:9',
  largeDesktopWidth: '762px',
  quality: 'good',
}

interface MappedProps {
  shouldShowSaleNumbers: boolean;
  windowSearch: string;
  location: Location;
}

interface Props {
  offer: App.Offer | App.OfferSummary;
  offerMetaData?: App.OfferListMetaData;
  bestPriceForDates: App.OfferAvailableRate;
  fetchingPriceForDates: boolean
  soldOut: boolean
  filters?: App.OfferListFilters;
  bestPricePackage: App.Package;
  eagerLoadFirstImage?: boolean;
  offerUrl: string;
  offerLinkIncludesFilters?: boolean;
  onImageChange?: (idx: number, image?: App.Image) => void
}

function SearchFlashOfferExpanded(props: Props & MappedProps) {
  const {
    offer,
    bestPriceForDates,
    soldOut,
    filters,
    bestPricePackage,
    eagerLoadFirstImage,
    offerUrl,
    shouldShowSaleNumbers,
    windowSearch,
    offerMetaData,
    offerLinkIncludesFilters,
    location,
    onImageChange,
  } = props

  const customerType = useMemo(() => getCustomerType(windowSearch), [windowSearch])
  const isMapPage = location.pathname.includes('/map')

  const [count, loading] = useHotelOfferSales(offer?.id, customerType)
  const { locationHeading, locationSubheading, badge, images: offerImages } = offer
  const locationName = [locationHeading, locationSubheading].filter(t => t).join(', ')
  const isCruise = offer.holidayTypes?.includes('Cruises')

  const checkInDate = filters?.checkIn ? moment(filters.checkIn) : undefined
  const cancellationPolicyType = bestPricePackage?.roomRate?.cancellationPolicy?.type

  const isMobile = useIsMobileScreen()

  const images = offerImages

  return (
    <Main className={cn({ mapTile: isMapPage })}>
      <MainImageSegment>
        <ImageCarousel
          images={images}
          eagerLoadFirstImage={eagerLoadFirstImage}
          imageParams={imageParams}
          onImageChange={onImageChange}
        />
        <OfferOverlayContainer>
          <OfferPartnershipLabel offer={offer} variant="icon" />
        </OfferOverlayContainer>
        {badge && <StyledOfferBadge badge={badge} dimensions={{ width: 86, height: 86 }} />}
        <StyledCSSBreakpoint only="mobile">
          <FloatingBookmarkContainer>
            <BookmarkButton offer={offer} />
          </FloatingBookmarkContainer>
        </StyledCSSBreakpoint>
      </MainImageSegment>
      <div>
        { shouldShowSaleNumbers && !loading && <PeopleLikeMeSalesBanner customerType={customerType} saleCount={count} />}
        <DetailSegment className={cn({ mapTile: isMapPage, flash: true })}>
          <TripGuard>
            <StyledCSSBreakpoint min="tablet">
              <FloatingBookmarkContainer>
                <BookmarkButton offer={offer} />
              </FloatingBookmarkContainer>
            </StyledCSSBreakpoint>
          </TripGuard>
          <SearchTilePropertyDetails
            location={locationName}
            cancellationPolicyType={cancellationPolicyType}
            offer={offer}
            checkInDate={checkInDate}
            filters={filters}
            offerUrl={offerUrl}
            maxInclusions={isMobile ? 3 : 5}
            bestPricePackage={bestPricePackage}
            bestPriceForDates={bestPriceForDates}
            maxInclusionLines={isMobile ? 5 : 8}
           />
          <SearchTilePriceDetails
            offer={offer}
            soldOut={soldOut}
            offerUrl={offerUrl}
            bestPriceForDates={bestPriceForDates}
            filters={filters}
            checkInDate={checkInDate}
            bestPricePackage={bestPricePackage}
          />

          {isCruise && <Caption colour="primary" variant="large" weight="bold">
            Sailing until {moment(offer.travelToDate).format(LONG_MONTH_LONG_YEAR)}
          </Caption>}

          {config.ENABLE_BUNDLE_AND_SAVE && offerMetaData?.bundleOfferId && (
            <ViewBundleOfferWrapper>
              <ViewBundleOffer offer={offer} filters={filters} offerMetaData={offerMetaData} offerLinkIncludesFilters={offerLinkIncludesFilters} />
            </ViewBundleOfferWrapper>
          )}
        </DetailSegment>
      </div>
    </Main>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  shouldShowSaleNumbers: state.router.location.pathname.endsWith('people-like-me'),
  windowSearch: state.router.location.search,
  location: state.router.location,
})

export default connect(mapStateToProps)(SearchFlashOfferExpanded)
